import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; message?: string };
      if (!res.ok) {
        setError(data.message ?? t("auth.errorForgot"));
        return;
      }
      setSuccess(true);
    } catch {
      setError(t("auth.errorForgot"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      <div
        className={cn(
          "hidden md:flex md:w-[40%] flex-col justify-center px-10 lg:px-16",
          "bg-[hsl(var(--login-brand-bg))] text-white"
        )}
      >
        <h2 className="text-2xl font-bold mb-8">TPT Brány</h2>
        <p className="text-lg leading-relaxed text-white/90 max-w-md">{t("auth.brandText")}</p>
      </div>

      <div className="flex-1 flex flex-col justify-center bg-background px-6 py-12 md:px-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <div className="md:hidden mb-8">
            <h2 className="text-xl font-bold text-foreground">TPT Brány</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("auth.brandText")}</p>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">{t("auth.forgotTitle")}</h1>
          <p className="text-muted-foreground mb-8">{t("auth.forgotIntro")}</p>

          {success ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-4">
                <p className="text-sm text-green-800 dark:text-green-300">{t("auth.forgotSuccess")}</p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
              >
                {t("auth.backToLogin")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-foreground mb-1">
                  {t("auth.email")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.placeholderEmail")}
                    required
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {submitting ? t("auth.sendSubmitting") : t("auth.send")}
              </button>
            </form>
          )}

          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("auth.backToLogin")}
          </Link>

          <p className="mt-12 text-center text-sm text-muted-foreground">{t("auth.copyright")}</p>
        </div>
      </div>
    </div>
  );
}
